import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// LocalStorage guard so the modal never flashes twice on the same
// device even if the network round-trip for `/user/heard-about` is slow.
const LS_KEY = 'zenscroll_heard_about_seen';

/**
 * "How did you hear about ZenScroll?" — post-signup survey.
 *
 * Renders as a full-screen card on mobile and a centered modal on
 * desktop.  Auto-detects if the survey has been submitted (or
 * skipped) for this account and self-hides.  Answers persist to the
 * user document via POST /api/user/heard-about and can never re-open
 * for the same account.
 */
export default function HeardAboutSurvey({ token }) {
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState('');
  const [otherText, setOtherText] = useState('');
  const [convinced, setConvinced] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch survey state on mount / whenever the auth token changes.
  useEffect(() => {
    if (!token) { setOpen(false); return undefined; }
    if (localStorage.getItem(LS_KEY) === 'true') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [stateRes, srcRes] = await Promise.all([
          axios.get(`${API}/user/heard-about`, { headers }),
          axios.get(`${API}/config/acquisition-sources`),
        ]);
        if (cancelled) return;
        if (stateRes.data?.completed || stateRes.data?.timestamp) {
          // Already submitted OR skipped — remember locally so we
          // don't re-fetch on every mount.
          localStorage.setItem(LS_KEY, 'true');
          setOpen(false);
        } else {
          setSources(srcRes.data?.sources || []);
          setOpen(true);
        }
      } catch (err) {
        // Soft-fail — never block the app because of the survey.
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[heard-about] init failed', err?.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const close = (persistDismissed) => {
    setOpen(false);
    if (persistDismissed) localStorage.setItem(LS_KEY, 'true');
  };

  const handleSkip = async () => {
    close(true);
    try {
      await axios.post(`${API}/user/heard-about/skip`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* best-effort — LS flag prevents re-prompt */ }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!source) {
      toast.error('Please pick one option.');
      return;
    }
    if (source === 'other' && !otherText.trim()) {
      toast.error('Please tell us where you found us.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/user/heard-about`, {
        source,
        other: otherText.trim().slice(0, 150),
        convinced: convinced || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Thanks for sharing!');
      close(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        // Already submitted from another tab / device — treat as done.
        close(true);
        return;
      }
      toast.error(err.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="heard-about-survey"
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-[#06070A]/85 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { /* backdrop click does not dismiss — must Skip or Continue */ }}
          />
          <motion.form
            onSubmit={handleSubmit}
            role="dialog"
            aria-labelledby="heard-about-title"
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border border-[#1C4749]/40 bg-gradient-to-b from-[#101F1D] to-[#0A1514] shadow-[0_30px_60px_rgba(0,0,0,0.5)] p-6 sm:p-8"
          >
            <button
              type="button"
              onClick={handleSkip}
              data-testid="heard-about-close-btn"
              aria-label="Skip survey"
              className="absolute top-3 right-3 text-white/40 hover:text-white/80 transition-colors p-1 rounded-full"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1C4749]/20 border border-[#1C4749]/40 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5EEAD4]">
              <Sparkles className="w-3 h-3" />
              Welcome
            </div>

            <h2
              id="heard-about-title"
              className="mt-4 text-2xl sm:text-3xl text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Welcome to ZenScroll!
            </h2>
            <p className="mt-2 text-sm sm:text-base text-white/75 leading-relaxed">
              We&rsquo;d love to know how you found us. Your answer helps us improve.
            </p>

            <fieldset className="mt-6">
              <legend className="text-sm font-semibold text-white/90 mb-3">
                How did you hear about ZenScroll?
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {sources.map((s) => {
                  const active = source === s.key;
                  return (
                    <label
                      key={s.key}
                      data-testid={`heard-about-option-${s.key}`}
                      className={`cursor-pointer text-sm px-3 py-2.5 rounded-xl border transition-colors ${
                        active
                          ? 'bg-[#1C4749]/30 border-[#5EEAD4]/60 text-white'
                          : 'bg-white/5 border-white/10 text-white/80 hover:bg-white/10'
                      }`}
                    >
                      <input
                        type="radio"
                        name="heard-about-source"
                        value={s.key}
                        checked={active}
                        onChange={() => setSource(s.key)}
                        className="sr-only"
                      />
                      {s.label}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {source === 'other' && (
              <div className="mt-4">
                <label htmlFor="heard-about-other" className="sr-only">
                  Tell us where you found us
                </label>
                <input
                  id="heard-about-other"
                  data-testid="heard-about-other-input"
                  type="text"
                  maxLength={150}
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Tell us where you found us…"
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-[#5EEAD4]/60"
                  autoFocus
                />
                <div className="mt-1 text-right text-[10px] text-white/40 tabular-nums">
                  {otherText.length}/150
                </div>
              </div>
            )}

            {/* Optional last-touch follow-up — only when a primary source is picked. */}
            {source && source !== 'other' && (
              <fieldset className="mt-5">
                <legend className="text-xs font-medium text-white/60 mb-2">
                  Optional — what convinced you to download today?
                </legend>
                <div className="flex flex-wrap gap-1.5">
                  {sources.filter((s) => s.key !== 'other').map((s) => {
                    const active = convinced === s.key;
                    return (
                      <button
                        type="button"
                        key={s.key}
                        data-testid={`heard-about-convinced-${s.key}`}
                        onClick={() => setConvinced(active ? '' : s.key)}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                          active
                            ? 'bg-[#5EEAD4]/20 border-[#5EEAD4]/60 text-[#5EEAD4]'
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            )}

            <div className="mt-7 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleSkip}
                data-testid="heard-about-skip-btn"
                className="text-sm text-white/60 hover:text-white transition-colors underline-offset-2 hover:underline"
              >
                Skip
              </button>
              <button
                type="submit"
                data-testid="heard-about-continue-btn"
                disabled={submitting || !source || (source === 'other' && !otherText.trim())}
                className="inline-flex items-center gap-2 bg-[#1C4749] text-white font-semibold rounded-full px-6 py-2.5 hover:bg-[#2A6F72] transition-colors shadow-[0_0_18px_rgba(28,71,73,0.45)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving…' : 'Continue'}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
